<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import { type SuperUserOrganisation as Organisation } from '@/types/generated/types.gen'
  import { OrganisationsService } from '@/types/generated/services.gen'
  import type { FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import CustomerDisplay from '@/components/admin/CustomerDisplay.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import LoginAsModal from './LoginAsModal.vue'
  import {
    LogIn,
    AlertTriangle,
    CreditCard,
    ArrowUp,
    ArrowDown,
    ArrowUpDown
  } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import type { Filter } from '@/components/admin/PaginatedTable.vue'

  const router = useRouter()
  const tableContainerRef = ref<HTMLElement | null>(null)
  const isCompact = ref(false)
  let resizeObserver: ResizeObserver | null = null

  watch(
    tableContainerRef,
    (el) => {
      resizeObserver?.disconnect()
      if (el) {
        resizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect.width ?? 0
          isCompact.value = width < 1000
        })
        resizeObserver.observe(el)
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    resizeObserver?.disconnect()
  })

  const loginAsModalOpen = ref(false)
  const selectedOrganisation = ref<Organisation | null>(null)

  const openLoginAsModal = (org: Organisation) => {
    selectedOrganisation.value = org
    loginAsModalOpen.value = true
  }

  const redirectTo = ref('')
  const tableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  // Fetch org data to derive dynamic filter options
  const countryOptions = ref<{ label: string; value: string }[]>([])
  const languageOptions = ref<{ label: string; value: string }[]>([])

  const LANGUAGE_LABELS: Record<string, string> = {
    de: 'Deutsch',
    fr: 'Französisch',
    en: 'Englisch',
    pl: 'Polnisch'
  }
  const COUNTRY_TO_ISO: Record<string, string> = {
    Deutschland: 'DE',
    Germany: 'DE',
    France: 'FR',
    Frankreich: 'FR',
    Polska: 'PL',
    Poland: 'PL',
    Österreich: 'AT',
    Austria: 'AT',
    Schweiz: 'CH',
    Switzerland: 'CH'
  }

  onMounted(async () => {
    const response = await OrganisationsService.organisationsList({ limit: 250 })
    const orgs = response.results ?? []

    // Extract unique countries
    const countrySet = new Map<string, string>()
    for (const org of orgs) {
      const country = (org as Organisation).country
      if (country) {
        const iso = COUNTRY_TO_ISO[country] || country
        if (!countrySet.has(iso)) countrySet.set(iso, country)
      }
    }
    countryOptions.value = Array.from(countrySet.entries()).map(([iso, name]) => ({
      label: name,
      value: iso
    }))

    // Extract unique languages
    const langSet = new Set<string>()
    for (const org of orgs) {
      const lang = (org as Organisation).default_language
      if (lang) langSet.add(lang)
    }
    languageOptions.value = Array.from(langSet).map((lang) => ({
      label: LANGUAGE_LABELS[lang] || lang,
      value: lang
    }))
  })

  const sortField = ref('customer_number')
  const sortOrder = ref<'asc' | 'desc'>('desc')

  const handleSort = (field: string) => {
    if (sortField.value === field) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortField.value = field
      sortOrder.value = 'asc'
    }
    nextTick(() => tableRef.value?.resetAndRefresh())
  }

  const getSortIcon = (field: string) => {
    if (sortField.value !== field) return ArrowUpDown
    return sortOrder.value === 'asc' ? ArrowUp : ArrowDown
  }

  const filterableFields = computed<Filter[]>(() => [
    {
      key: 'integrations',
      label: 'Integrations',
      type: 'multi-select',
      options: [
        { label: 'Stripe', value: 'stripe' },
        { label: 'HubSpot', value: 'hubspot' },
        { label: 'Platform', value: 'platform' },
        { label: 'Platform Sub', value: 'platformSub' },
        { label: 'Phonebot Sub', value: 'phonebotSub' },
        { label: 'Integrity Errors', value: 'integrityErrors' }
      ]
    },
    {
      key: 'phonebotType',
      label: 'Phonebot',
      type: 'multi-select',
      options: [
        { label: 'Self-Service', value: 'self_service' },
        { label: 'Managed', value: 'managed' }
      ]
    },
    {
      key: 'country',
      label: 'Country',
      type: 'multi-select',
      options: countryOptions.value
    },
    {
      key: 'defaultLanguage',
      label: 'Language',
      type: 'multi-select',
      options: languageOptions.value
    }
  ])

  // Helper to get phonebot display text
  const getPhonebotType = (org: Organisation): string | null => {
    if (org.is_phonebot_self_service_customer) {
      return 'Self-Service'
    } else if (org.enable_voice) {
      return 'Managed'
    }
    return null
  }

  // Helper to get language display text
  const getLanguageDisplay = (org: Organisation): string => {
    const languageMap: Record<string, string> = {
      de: 'Deutsch',
      fr: 'Französisch',
      en: 'Englisch'
    }
    return languageMap[org.default_language ?? 'de'] ?? 'Deutsch'
  }

  const fetchOrganisations = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const filters = options?.filters || []
    const getFilter = (key: string) => filters.find((f) => f.key === key)?.value
    const orderingParam = sortOrder.value === 'desc' ? `-${sortField.value}` : sortField.value

    const response = await OrganisationsService.organisationsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      ordering: orderingParam,
      integrations: getFilter('integrations'),
      country: getFilter('country'),
      defaultLanguage: getFilter('defaultLanguage'),
      phonebotType: getFilter('phonebotType')
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST }
          ]"
        />

        <div class="flex items-center justify-end gap-x-2">
          <router-link
            :to="ROUTES.ADMIN.CUSTOMERS.CUSTOMER_CREATE"
            data-cy="create-customer-button"
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Create New Customer
          </router-link>
        </div>
      </div>
      <div
        ref="tableContainerRef"
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <PaginatedTableView
          ref="tableRef"
          :fetchData="fetchOrganisations"
          searchable-fields="Uuid, customer number, customer name"
          :filterableFields="filterableFields"
        >
          <template #table-head>
            <TableCell class="w-[1%] whitespace-nowrap px-5">
              <button
                class="inline-flex items-center gap-1 font-medium"
                @click="handleSort('customer_number')"
              >
                #
                <component
                  :is="getSortIcon('customer_number')"
                  class="h-4 w-4"
                />
              </button>
            </TableCell>
            <TableCell class="whitespace-nowrap px-5">
              <button
                class="inline-flex items-center gap-1 font-medium"
                @click="handleSort('name')"
              >
                Name
                <component
                  :is="getSortIcon('name')"
                  class="h-4 w-4"
                />
              </button>
            </TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5">Email</TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5">Health</TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5">Stripe</TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5">HubSpot</TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5">Platform</TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5">Phonebot</TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5">Language</TableCell>
            <TableCell class="w-[1%] whitespace-nowrap px-5"></TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="org in items"
              :key="org.uuid"
              class="cursor-pointer"
              data-cy="customer-row"
              @click="
                router.push(
                  ROUTES.ADMIN.CUSTOMERS.CUSTOMER_DETAIL.replace(
                    ':uuid',
                    (org as Organisation).uuid
                  )
                )
              "
            >
              <TableCell class="text-center">
                {{ (org as Organisation).customer_number }}
              </TableCell>
              <TableCell class="whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <CustomerDisplay
                    :org="org as Organisation"
                    :small="isCompact"
                  />
                  <AlertTriangle
                    v-if="(org as Organisation).has_integrity_errors"
                    class="h-4 w-4 shrink-0 text-amber-500"
                    title="Integrity errors detected"
                  />
                </div>
              </TableCell>
              <TableCell class="whitespace-nowrap text-center">
                {{ (org as Organisation).email }}
              </TableCell>
              <TableCell class="text-center">
                {{ (org as Organisation).vitally_health_score ?? 'N/A' }}
              </TableCell>
              <TableCell class="text-center">
                <span
                  :class="
                    (org as Organisation).stripe_customer_uuid
                      ? 'text-green-700'
                      : 'text-orange-700'
                  "
                >
                  {{ (org as Organisation).stripe_customer_uuid ? 'Yes' : 'No' }}
                </span>
              </TableCell>
              <TableCell class="text-center">
                <span
                  :class="
                    (org as Organisation).hubspot_company_uuid
                      ? 'text-green-700'
                      : 'text-orange-700'
                  "
                >
                  {{ (org as Organisation).hubspot_company_uuid ? 'Yes' : 'No' }}
                </span>
              </TableCell>
              <TableCell class="text-center">
                <div
                  v-if="(org as Organisation).enable_cases"
                  class="inline-flex items-center gap-2"
                >
                  <span class="text-green-700">Yes</span>
                  <CreditCard
                    v-if="(org as Organisation).platform_stripe_subscription_uuid"
                    class="h-4 w-4 text-green-600"
                    title="Stripe subscription connected"
                  />
                  <CreditCard
                    v-else
                    class="h-4 w-4 text-amber-500"
                    title="No Stripe subscription"
                  />
                </div>
                <span
                  v-else
                  class="text-orange-700"
                >
                  No
                </span>
              </TableCell>
              <TableCell class="text-center">
                <div
                  v-if="getPhonebotType(org as Organisation)"
                  class="inline-flex items-center gap-2"
                >
                  <span
                    :class="
                      (org as Organisation).is_phonebot_self_service_customer
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    "
                  >
                    {{ getPhonebotType(org as Organisation) }}
                  </span>
                  <CreditCard
                    v-if="(org as Organisation).phonebot_stripe_subscription_uuid"
                    class="h-4 w-4 text-green-600"
                    title="Stripe subscription connected"
                  />
                  <CreditCard
                    v-else
                    class="h-4 w-4 text-amber-500"
                    title="No Stripe subscription"
                  />
                </div>
                <span
                  v-else
                  class="text-gray-400"
                >
                  -
                </span>
              </TableCell>
              <TableCell class="text-center">
                {{ getLanguageDisplay(org as Organisation) }}
              </TableCell>
              <TableCell
                class="text-center"
                @click.stop
              >
                <TableOptionsButton
                  data-cy="customer-options-button"
                  :options="[
                    {
                      label: 'Login As',
                      dataCy: 'menu-login-as',
                      icon: LogIn,
                      onClick: () => openLoginAsModal(org as Organisation)
                    },
                    {
                      label: 'Chatbot',
                      dataCy: 'menu-chatbot',
                      to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_BOT_SETTINGS.replace(
                        ':uuid',
                        (org as Organisation).uuid
                      )
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
    <!-- Login As Modal -->
    <LoginAsModal
      v-model:open="loginAsModalOpen"
      :organisation="selectedOrganisation"
    />
  </div>
</template>

<style scoped></style>
